import React, { useEffect, useState } from 'react'
import {
  OrderPreview,
  OrderSide,
  OrderType,
  TimeInForce,
  tradingServices,
  TradingPosition,
} from '../../services/trading-service'
import { SymbolSearch } from './symbol-search'
import { easternClock } from './market-status'
import {
  allowedTimeInForce,
  allowsDollars,
  emptyOrderForm,
  formProblem,
  normalizeForm,
  ORDER_TYPE_HELP,
  ORDER_TYPE_LABEL,
  OrderForm,
  TIME_IN_FORCE_LABEL,
  toPayload,
  usd,
  usesLimit,
  usesStop,
} from './order-rules'

export type SelectedAsset = { symbol: string; name: string }

const errorMessage = (res: any, fallback: string): string =>
  res?.data?.error?.message || res?.data?.message || fallback

const shares = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${Number(n.toFixed(6))} sh`

const RISK_DISCLOSURE =
  'Investing in stocks involves risk, including the possible loss of principal. Prices can move quickly and orders may fill at a different price than estimated. Past performance does not guarantee future results.'

/** The review step: what the order will do, shown before it is sent. */
const OrderReview = ({
  preview,
  busy,
  onConfirm,
  onEdit,
}: {
  preview: OrderPreview
  busy: boolean
  onConfirm: () => void
  onEdit: () => void
}) => {
  const o = preview.order
  const rows: Array<[string, React.ReactNode]> = [
    ['Stock', o.symbol],
    ['Side', o.side === 'buy' ? 'Buy' : 'Sell'],
    ['Order type', ORDER_TYPE_LABEL[o.type]],
    ['Time in force', TIME_IN_FORCE_LABEL[o.timeInForce]],
    [o.notional !== null ? 'Amount' : 'Shares', o.notional !== null ? usd(o.notional) : o.qty],
  ]
  if (o.limitPrice !== null) rows.push(['Limit price', usd(o.limitPrice)])
  if (o.stopPrice !== null) rows.push(['Stop price', usd(o.stopPrice)])
  if (o.trailPrice !== null) rows.push(['Trails by', usd(o.trailPrice)])
  if (o.trailPercent !== null) rows.push(['Trails by', `${o.trailPercent}%`])
  if (o.extendedHours) rows.push(['Extended hours', 'Yes'])
  rows.push(
    ['Estimated price', usd(preview.estimatedPrice)],
    ['Estimated shares', shares(preview.estimatedQty)],
    [o.side === 'buy' ? 'Estimated cost' : 'Estimated proceeds', usd(preview.estimatedTotal)],
    ['Commission', preview.fee > 0 ? usd(preview.fee) : 'Free'],
  )
  if (o.side === 'buy') {
    rows.push(
      ['Buying power now', usd(preview.buyingPowerBefore)],
      ['Buying power after', usd(preview.buyingPowerAfter)],
    )
  } else if (preview.sharesHeld !== null) {
    rows.push(['Shares you can sell', shares(preview.sharesHeld)])
  }

  return (
    <div className='trading-page__review' data-testid='order-review'>
      <h3>Review your order</h3>
      <dl>
        {rows.map(([label, value], i) => (
          <div key={`${label}-${i}`}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {preview.delayMinutes > 0 && preview.quoteAsOf ? (
        <p className='trading-page__muted'>
          Prices delayed {preview.delayMinutes} minutes · as of {easternClock(preview.quoteAsOf)}
        </p>
      ) : null}
      {preview.warnings.length > 0 ? (
        <ul className='trading-page__warnings'>
          {preview.warnings.map((w) => (
            <li key={w.code}>{w.message}</li>
          ))}
        </ul>
      ) : null}
      {o.side === 'sell' ? (
        <p className='trading-page__muted'>
          Sells may carry small regulatory fees (SEC and FINRA), taken from the proceeds.
        </p>
      ) : null}
      <p className='trading-page__disclaimer'>{RISK_DISCLOSURE}</p>
      <div className='trading-page__actions'>
        <button type='button' className='trading-page__btn' disabled={busy} onClick={onConfirm}>
          {busy ? 'Placing order…' : 'Confirm order'}
        </button>
        <button
          type='button'
          className='trading-page__btn is-secondary'
          disabled={busy}
          onClick={onEdit}
        >
          Edit
        </button>
      </div>
    </div>
  )
}

/**
 * Order entry with a review step: the order is estimated by the server and
 * only sent after the investor confirms. Without `onSelect` the stock is
 * fixed (company page); otherwise a search picks it.
 */
const OrderTicket = ({
  selected,
  onSelect,
  onPlaced,
  positions,
  side,
}: {
  selected: SelectedAsset | null
  onSelect?: (asset: SelectedAsset | null) => void
  onPlaced: () => void
  positions: TradingPosition[]
  /** Pre-selects buy or sell, e.g. from a company page button. */
  side?: OrderSide
}) => {
  const [form, setForm] = useState<OrderForm>(() => emptyOrderForm(side))
  const [quote, setQuote] = useState<{ bid?: number; ask?: number } | null>(null)
  const [preview, setPreview] = useState<OrderPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)

  const sym = selected?.symbol ?? ''
  const position = positions.find((p) => p.symbol === sym)
  const sellable = position ? Number(position.qty_available ?? position.qty) : 0

  useEffect(() => {
    if (side) setForm((f) => normalizeForm({ ...f, side }))
    setPreview(null)
  }, [side])

  useEffect(() => {
    setMessage(null)
    setPreview(null)
    setQuote(null)
    if (!sym) return
    let cancelled = false
    const lookup = async () => {
      const res = await tradingServices.getQuotes([sym])
      const q = res?.data?.quotes?.[sym]
      if (cancelled) return
      if (res?.status === 200 && q) setQuote({ bid: q.bp, ask: q.ap })
    }
    void lookup()
    return () => {
      cancelled = true
    }
  }, [sym])

  const update = (patch: Partial<OrderForm>) => {
    setForm((f) => normalizeForm({ ...f, ...patch }))
    setPreview(null)
    setMessage(null)
  }

  const problem = sym ? formProblem(form, form.side === 'sell' ? sellable : null) : null
  const touched = form.amount !== ''

  const review = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!sym || problem) return
    setBusy(true)
    setMessage(null)
    const res = await tradingServices.previewOrder(toPayload(sym, form))
    setBusy(false)
    if (res?.status === 200) setPreview(res.data)
    else setMessage({ kind: 'error', text: errorMessage(res, 'Could not review this order.') })
  }

  const confirm = async () => {
    setBusy(true)
    setMessage(null)
    const res = await tradingServices.placeOrder(toPayload(sym, form))
    setBusy(false)
    setPreview(null)
    if (res?.status === 201) {
      setMessage({ kind: 'ok', text: `Order submitted (${res.data.status}).` })
      setForm((f) => ({ ...emptyOrderForm(f.side) }))
      onPlaced()
    } else {
      setMessage({ kind: 'error', text: errorMessage(res, 'Order was not accepted.') })
    }
  }

  return (
    <form className='trading-page__card' onSubmit={review} data-testid='trading-order-ticket'>
      <h2>{onSelect ? 'Place an order' : `Trade ${sym}`}</h2>
      {onSelect ? (
        <div className='trading-page__row'>
          <SymbolSearch selected={selected} onSelect={onSelect} />
        </div>
      ) : null}
      {quote ? (
        <p className='trading-page__quote'>
          Bid {usd(quote.bid || undefined)} · Ask {usd(quote.ask || undefined)}
        </p>
      ) : null}

      {preview ? (
        <OrderReview
          preview={preview}
          busy={busy}
          onConfirm={() => void confirm()}
          onEdit={() => setPreview(null)}
        />
      ) : (
        <>
          <div className='trading-page__segmented' role='group' aria-label='Side'>
            {(['buy', 'sell'] as OrderSide[]).map((s) => (
              <button
                key={s}
                type='button'
                aria-pressed={form.side === s}
                className={form.side === s ? 'is-active' : ''}
                onClick={() => update({ side: s })}
              >
                {s === 'buy' ? 'Buy' : 'Sell'}
              </button>
            ))}
          </div>
          {form.side === 'sell' && sym ? (
            <p className='trading-page__hint'>
              {sellable > 0 ? (
                <>
                  You can sell {sellable} shares.{' '}
                  <button
                    type='button'
                    className='trading-page__link'
                    onClick={() => update({ mode: 'qty', amount: String(sellable) })}
                  >
                    Sell all
                  </button>
                </>
              ) : (
                `You have no ${sym} shares to sell.`
              )}
            </p>
          ) : null}
          <div className='trading-page__row'>
            <label className='trading-page__field'>
              <span>Order type</span>
              <select
                value={form.type}
                onChange={(e) => update({ type: e.target.value as OrderType })}
              >
                {(Object.keys(ORDER_TYPE_LABEL) as OrderType[]).map((t) => (
                  <option key={t} value={t}>
                    {ORDER_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </label>
            <label className='trading-page__field'>
              <span>Order by</span>
              <select
                value={form.mode}
                onChange={(e) => update({ mode: e.target.value as OrderForm['mode'] })}
                disabled={!allowsDollars(form.type)}
              >
                <option value='qty'>Shares</option>
                <option value='notional'>Dollars</option>
              </select>
            </label>
            <label className='trading-page__field'>
              <span>{form.mode === 'qty' ? 'Shares' : 'Amount (USD)'}</span>
              <input
                type='number'
                min='0'
                step='any'
                value={form.amount}
                onChange={(e) => update({ amount: e.target.value })}
              />
            </label>
          </div>
          <p className='trading-page__hint'>{ORDER_TYPE_HELP[form.type]}</p>
          <div className='trading-page__row'>
            {usesStop(form.type) ? (
              <label className='trading-page__field'>
                <span>Stop price</span>
                <input
                  type='number'
                  min='0'
                  step='any'
                  value={form.stopPrice}
                  onChange={(e) => update({ stopPrice: e.target.value })}
                />
              </label>
            ) : null}
            {usesLimit(form.type) ? (
              <label className='trading-page__field'>
                <span>Limit price</span>
                <input
                  type='number'
                  min='0'
                  step='any'
                  value={form.limitPrice}
                  onChange={(e) => update({ limitPrice: e.target.value })}
                />
              </label>
            ) : null}
            {form.type === 'trailing_stop' ? (
              <>
                <label className='trading-page__field'>
                  <span>Trail by</span>
                  <select
                    value={form.trailBy}
                    onChange={(e) => update({ trailBy: e.target.value as OrderForm['trailBy'] })}
                  >
                    <option value='percent'>Percent</option>
                    <option value='price'>Dollars</option>
                  </select>
                </label>
                <label className='trading-page__field'>
                  <span>{form.trailBy === 'percent' ? 'Trail (%)' : 'Trail (USD)'}</span>
                  <input
                    type='number'
                    min='0'
                    step='any'
                    value={form.trail}
                    onChange={(e) => update({ trail: e.target.value })}
                  />
                </label>
              </>
            ) : null}
            <label className='trading-page__field'>
              <span>Time in force</span>
              <select
                value={form.timeInForce}
                onChange={(e) => update({ timeInForce: e.target.value as TimeInForce })}
              >
                {allowedTimeInForce(form).map((t) => (
                  <option key={t} value={t}>
                    {TIME_IN_FORCE_LABEL[t]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {form.type === 'limit' ? (
            <label className='trading-page__check'>
              <input
                type='checkbox'
                checked={form.extendedHours}
                onChange={(e) => update({ extendedHours: e.target.checked })}
              />
              <span>
                Also trade in extended hours (pre-market and after hours, day orders only)
              </span>
            </label>
          ) : null}
          {problem && touched ? (
            <p className='trading-page__error' role='alert'>
              {problem}
            </p>
          ) : null}
        </>
      )}

      {message ? (
        <p
          className={message.kind === 'ok' ? 'trading-page__ok' : 'trading-page__error'}
          role={message.kind === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      ) : null}
      {preview ? null : (
        <button
          type='submit'
          className='trading-page__btn'
          disabled={busy || !sym || Boolean(problem)}
        >
          {busy
            ? 'Checking…'
            : `Review ${form.side === 'buy' ? 'buy' : 'sell'}${sym ? ` ${sym}` : ''}`}
        </button>
      )}
    </form>
  )
}

export { OrderTicket }
